'use client'

/* Operate: extend OW's deposit flow with the supplied Jupiter amount-first anatomy.
 * OW Inter, theme-aware neutral surfaces, purple action, Bitcoin orange asset.
 * First viewport: deposit tabs, stacked send/receive fields, route, wallet, one CTA.
 * Funds only move when the user sends them from their source wallet. */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import QRCode from 'react-qr-code'
import copy from 'copy-to-clipboard'
import {
	ArrowDown,
	ArrowLeft,
	ArrowUpRight,
	Check,
	ChevronDown,
	Clock3,
	Copy,
	ExternalLink,
	Loader2,
	RefreshCw,
	Search,
	Wallet as WalletIcon
} from 'lucide-react'
import Wallet from '@/utils/wallet'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import event from '@/utils/event'
import { toast } from '@/components/ui/use-toast'
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import ScrollLoadMore from '@/components/scroll-load-more'
import {
	swapRouter,
	normalizeSwapAmount,
	networkLabel,
	getDepositAmountExact,
	getPayoutAmount,
	withSwapQuote,
	mergeExchangeUpdate,
	loadActiveSwap,
	saveActiveSwap,
	clearActiveSwap,
	isFinalSwap,
	isAwaitingSwapDeposit,
	swapFundsMoved,
	type SwapCurrency,
	type SwapQuote,
	type SwapExchange,
	type SwapProvider
} from './model'
import styles from './swap.module.css'

const statusLabels: Record<string, string> = {
	waiting: 'Waiting for your deposit',
	confirming: 'Confirming your deposit',
	exchanging: 'Swapping to Bitcoin',
	sending: 'Sending BTC to your wallet',
	finished: 'BTC sent to your wallet',
	failed: 'Swap needs attention',
	refunded: 'Funds refunded',
	expired: 'Deposit window expired',
	overdue: 'Deposit window expired',
	new: 'Waiting for your deposit',
	hold: 'Provider review required',
	verifying: 'Provider review required'
}
const connect = () => event.emit('command', { id: 'connect-wallet' })
const keyOf = (coin: Pick<SwapCurrency, 'ticker' | 'network'>) => `${coin.ticker}:${coin.network}`
const formatBtc = (value: number) => value.toLocaleString('en-US', { maximumFractionDigits: 8 })
const shortAddress = (value: string) => `${value.slice(0, 9)}…${value.slice(-7)}`
const messageOf = (error: unknown) =>
	error instanceof Error ? error.message : 'Something went wrong. Please try again.'

// ChangeNOW's token icons bake a small network mark into the coin, where it is hard
// to see. For the main stablecoins on the main chains, draw the plain coin with a
// separate network badge instead (icons: cryptocurrency-icons, CC0, in
// public/swap-coins). Anything else, or a load failure, keeps the provider icon.
// BTC always uses the standard logo from the same set.
const PLAIN_COINS = new Set(['usdc', 'usdt', 'dai'])
const NETWORK_COIN: Record<string, string> = {
	eth: 'eth',
	sol: 'sol',
	bsc: 'bnb',
	trx: 'trx',
	matic: 'matic',
	avaxc: 'avax'
}
const plainCoinIcon = (ticker: string) => `/swap-coins/${ticker}.svg`

function CoinIcon({ coin }: { coin?: SwapCurrency }) {
	const [failed, setFailed] = useState(false)
	const [badgeFailed, setBadgeFailed] = useState(false)
	useEffect(() => {
		setFailed(false)
		setBadgeFailed(false)
	}, [coin?.image, coin?.ticker, coin?.network])
	if (!coin || coin.ticker === 'btc')
		return (
			<span className={styles.bitcoin} aria-hidden="true">
				<img src={plainCoinIcon('btc')} alt="" width={30} height={30} />
			</span>
		)
	const chain = NETWORK_COIN[coin.network]
	if (chain && PLAIN_COINS.has(coin.ticker) && !badgeFailed)
		return (
			<span className={styles.coinStack}>
				<span className={styles.coinIcon}>
					<img
						src={plainCoinIcon(coin.ticker)}
						alt=""
						width={30}
						height={30}
						onError={() => setBadgeFailed(true)}
					/>
				</span>
				<img
					className={styles.networkBadge}
					src={plainCoinIcon(chain)}
					alt=""
					width={16}
					height={16}
					onError={() => setBadgeFailed(true)}
				/>
			</span>
		)
	return (
		<span className={styles.coinIcon}>
			{coin.image?.startsWith('https://') && !failed ? (
				<img src={coin.image} alt="" width={30} height={30} onError={() => setFailed(true)} />
			) : (
				<span aria-hidden="true">{coin.ticker.slice(0, 1).toUpperCase()}</span>
			)}
		</span>
	)
}

export default function BtcDeposit() {
	const loggedIn = useLoggedIn()
	const address = loggedIn ? Wallet.balanceAddress() : ''
	return (
		<main className={styles.page}>
			<Link href="/wallet" className={styles.back}>
				<ArrowLeft size={16} /> Wallet
			</Link>
			<header className={styles.heading}>
				<h1>Swap</h1>
				<p>Bring your crypto. Receive Bitcoin.</p>
			</header>
			<DepositPanel key={address || 'disconnected'} address={address} />
		</main>
	)
}

/** Embedded in the BTC market's Buy / Sell / Swap panel. */
export function BtcSwapWidget() {
	const loggedIn = useLoggedIn()
	const address = loggedIn ? Wallet.balanceAddress() : ''
	return (
		<section className={styles.embedded} aria-label="Swap crypto to Bitcoin">
			<header className={styles.embeddedHeading}>
				<h2>Swap to BTC</h2>
				<p>Send crypto. Receive Bitcoin in your wallet.</p>
			</header>
			<SwapPanel key={address || 'disconnected'} address={address} />
		</section>
	)
}

export function DepositPanel({ address }: { address: string }) {
	const [tab, setTab] = useState<'swap' | 'receive'>('swap')
	return (
		<section className={styles.panel} aria-label="Deposit into your wallet">
			<div className={styles.tabs} role="tablist" aria-label="Deposit method">
				<button
					type="button"
					role="tab"
					id="swap-tab"
					aria-selected={tab === 'swap'}
					aria-controls="swap-panel"
					tabIndex={tab === 'swap' ? 0 : -1}
					onKeyDown={(e) => {
						if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
							setTab('receive')
							document.getElementById('receive-tab')?.focus()
						}
					}}
					onClick={() => setTab('swap')}
				>
					Swap to BTC
				</button>
				<button
					type="button"
					role="tab"
					id="receive-tab"
					aria-selected={tab === 'receive'}
					aria-controls="receive-panel"
					tabIndex={tab === 'receive' ? 0 : -1}
					onKeyDown={(e) => {
						if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
							setTab('swap')
							document.getElementById('swap-tab')?.focus()
						}
					}}
					onClick={() => setTab('receive')}
				>
					Receive BTC
				</button>
			</div>
			<div role="tabpanel" id="swap-panel" aria-labelledby="swap-tab" hidden={tab !== 'swap'}>
				<SwapPanel address={address} />
			</div>
			<div
				role="tabpanel"
				id="receive-panel"
				aria-labelledby="receive-tab"
				hidden={tab !== 'receive'}
			>
				<ReceiveBitcoin address={address} />
			</div>
		</section>
	)
}

function copyValue(value: string, label: string) {
	if (copy(value)) toast({ title: `${label} copied` })
	else toast({ title: 'Could not copy', description: 'Select the address and copy it manually.' })
}

function ReceiveBitcoin({ address }: { address: string }) {
	return (
		<div className={styles.receive}>
			<CoinIcon />
			<h2>Receive Bitcoin</h2>
			<p>Send BTC on the Bitcoin network to your wallet.</p>
			{address ? (
				<>
					<div className={styles.qr}>
						<QRCode value={address} size={176} />
					</div>
					<code className={styles.fullAddress}>{address}</code>
					<button
						className={styles.primary}
						type="button"
						onClick={() => copyValue(address, 'Bitcoin address')}
					>
						<Copy size={17} /> Copy BTC address
					</button>
				</>
			) : (
				<button className={styles.primary} type="button" onClick={connect}>
					Connect wallet
				</button>
			)}
		</div>
	)
}

function SwapPanel({ address }: { address: string }) {
	const [currencies, setCurrencies] = useState<SwapCurrency[]>([])
	const [catalogError, setCatalogError] = useState('')
	const [catalogLoading, setCatalogLoading] = useState(true)
	const [catalogRetry, setCatalogRetry] = useState(0)
	const [sourceKey, setSourceKey] = useState('usdc:eth')
	const [pickerOpen, setPickerOpen] = useState(false)
	const [amount, setAmount] = useState('')
	const [minimumState, setMinimum] = useState<{ key: string; value: number } | null>(null)
	const [quoteState, setQuoteState] = useState<{
		key: string
		quote: SwapQuote
		at: number
	} | null>(null)
	const [quoteLoading, setQuoteLoading] = useState(false)
	const [quoteError, setQuoteError] = useState('')
	const [quoteRetry, setQuoteRetry] = useState(0)
	const [provider, setProvider] = useState<SwapProvider | null>(null)
	const [review, setReview] = useState(false)
	const [refund, setRefund] = useState('')
	const [refundMemo, setRefundMemo] = useState('')
	const [creating, setCreating] = useState(false)
	const [createError, setCreateError] = useState('')
	const [exchange, setExchange] = useState<SwapExchange | null>(null)
	const [orderSaved, setOrderSaved] = useState(true)
	const [restored, setRestored] = useState(false)
	const createLock = useRef(false)
	const mounted = useRef(true)
	useEffect(() => {
		mounted.current = true
		return () => {
			mounted.current = false
		}
	}, [])
	useEffect(() => {
		setExchange(address ? loadActiveSwap(address) : null)
		setRestored(true)
	}, [address])
	const source = currencies.find((coin) => keyOf(coin) === sourceKey)
	const minimum = minimumState?.key === sourceKey ? minimumState.value : null
	const normalizedAmount = normalizeSwapAmount(amount)
	const requestKey = `${sourceKey}:${normalizedAmount}`
	const quote = quoteState?.key === requestKey ? quoteState.quote : null
	const route = quote?.routes.find((item) => item.provider === provider) ?? quote?.routes[0] ?? null
	const belowMinimum = !!normalizedAmount && minimum !== null && Number(normalizedAmount) < minimum
	const canReview = !!address && !!source && !!route && !belowMinimum && !quoteLoading && restored
	useEffect(() => {
		if (!quoteState) return
		const timer = setTimeout(
			() => setQuoteState(null),
			Math.max(0, 60_000 - (Date.now() - quoteState.at))
		)
		return () => clearTimeout(timer)
	}, [quoteState])

	useEffect(() => {
		const controller = new AbortController()
		setCatalogLoading(true)
		setCatalogError('')
		swapRouter
			.currencies(controller.signal)
			.then(({ currencies: items }) => {
				if (controller.signal.aborted) return
				setCurrencies(items)
				if (items.length)
					setSourceKey((current) =>
						items.some((coin) => keyOf(coin) === current)
							? current
							: keyOf(items.find((coin) => coin.ticker === 'usdc') ?? items[0])
					)
			})
			.catch((error) => {
				if (!controller.signal.aborted) setCatalogError(messageOf(error))
			})
			.finally(() => {
				if (!controller.signal.aborted) setCatalogLoading(false)
			})
		return () => controller.abort()
	}, [catalogRetry])

	useEffect(() => {
		setMinimum(null)
		if (!source) return
		const controller = new AbortController()
		swapRouter
			.minimum(source, controller.signal)
			.then((value) => {
				if (!controller.signal.aborted) setMinimum({ key: keyOf(source), value: value.minAmount })
			})
			.catch(() => {})
		return () => controller.abort()
	}, [source])

	useEffect(() => {
		setQuoteState(null)
		setQuoteError('')
		setQuoteLoading(false)
		if (!source || !normalizedAmount || belowMinimum || exchange) return
		const controller = new AbortController()
		setQuoteLoading(true)
		const timer = setTimeout(() => {
			swapRouter
				.quote(source, normalizedAmount, controller.signal)
				.then((value) => {
					if (!controller.signal.aborted)
						setQuoteState({ key: requestKey, quote: value, at: Date.now() })
				})
				.catch((error) => {
					if (!controller.signal.aborted) setQuoteError(messageOf(error))
				})
				.finally(() => {
					if (!controller.signal.aborted) setQuoteLoading(false)
				})
		}, 400)
		return () => {
			clearTimeout(timer)
			controller.abort()
		}
	}, [source, normalizedAmount, requestKey, belowMinimum, quoteRetry, exchange])
	useEffect(() => {
		if (!normalizedAmount || exchange) return
		const timer = setInterval(() => setQuoteRetry((value) => value + 1), 30_000)
		return () => clearInterval(timer)
	}, [normalizedAmount, exchange])

	const selectSource = (coin: SwapCurrency) => {
		setSourceKey(keyOf(coin))
		setProvider(null)
		setRefund('')
		setRefundMemo('')
		setReview(false)
		setCreateError('')
		setPickerOpen(false)
	}
	const create = async () => {
		if (
			createLock.current ||
			!canReview ||
			!source ||
			!route ||
			!normalizedAmount ||
			!refund.trim()
		)
			return
		if (!quoteState || Date.now() - quoteState.at >= 60_000) {
			setQuoteRetry((value) => value + 1)
			return
		}
		createLock.current = true
		setCreating(true)
		setCreateError('')
		try {
			const created = await swapRouter.create({
				asset: source,
				fromAmount: normalizedAmount,
				address,
				refundAddress: refund.trim(),
				refundExtraId: refundMemo.trim(),
				provider: route.provider
			})
			const order = withSwapQuote(created, route)
			// Save under the original destination even if the user changes wallets during the request.
			const saved = saveActiveSwap(address, order)
			if (!saved && mounted.current)
				toast({
					title: 'Keep this tab open',
					description: 'Your browser could not save this swap. Copy the order ID before leaving.'
				})
			if (mounted.current) {
				setOrderSaved(saved)
				setExchange(order)
			}
		} catch (error) {
			if (mounted.current) setCreateError(messageOf(error))
		} finally {
			createLock.current = false
			if (mounted.current) setCreating(false)
		}
	}

	if (exchange)
		return (
			<SwapProgress
				initial={exchange}
				address={address}
				persisted={orderSaved}
				onNew={() => {
					clearActiveSwap(address)
					setExchange(null)
					setReview(false)
					setAmount('')
					setQuoteState(null)
					setCreateError('')
				}}
			/>
		)

	return (
		<div className={styles.swap}>
			{review && (
				<button
					className={styles.back}
					type="button"
					onClick={() => setReview(false)}
					disabled={creating}
				>
					<ArrowLeft size={16} /> Edit swap
				</button>
			)}
			<div className={styles.amounts}>
				<div className={styles.sendBox}>
					<label className={styles.fieldLabel} htmlFor="swap-amount">
						You send
					</label>
					<div className={styles.amountRow}>
						<input
							id="swap-amount"
							aria-describedby="swap-amount-help"
							inputMode="decimal"
							autoComplete="off"
							placeholder="0.00"
							value={amount}
							readOnly={review}
							maxLength={40}
							onChange={(e) => {
								setAmount(e.target.value)
								setCreateError('')
							}}
						/>
						<button
							className={styles.coinButton}
							type="button"
							disabled={review || catalogLoading || !currencies.length}
							onClick={() => setPickerOpen(true)}
							aria-label="Select source coin and network"
						>
							{catalogLoading ? (
								<Loader2 className={styles.spinner} size={20} />
							) : (
								<CoinIcon coin={source} />
							)}
							<span>{source?.ticker.toUpperCase() ?? 'Select'}</span>
							<ChevronDown size={16} />
						</button>
					</div>
					<div className={styles.amountMeta} id="swap-amount-help">
						<span>{source ? networkLabel(source.network) : 'Choose a coin and network'}</span>
						{minimum !== null && source && (
							<span>
								Min. {minimum.toLocaleString('en-US', { maximumFractionDigits: 12 })}{' '}
								{source.ticker.toUpperCase()}
							</span>
						)}
					</div>
				</div>
				<div className={styles.direction} aria-label="Swap into Bitcoin">
					<ArrowDown size={18} />
				</div>
				<div className={styles.receiveBox}>
					<div className={styles.fieldLabel}>
						You receive <span>Estimated</span>
					</div>
					<div className={styles.amountRow}>
						<output
							className={!route ? styles.placeholder : ''}
							aria-live="polite"
							aria-label="Estimated Bitcoin received"
						>
							{quoteLoading ? (
								<Loader2 size={28} className={styles.spinner} />
							) : route ? (
								formatBtc(route.toAmount)
							) : (
								'0.00'
							)}
						</output>
						<div className={styles.fixedCoin}>
							<CoinIcon />
							<span>BTC</span>
						</div>
					</div>
					<div className={styles.amountMeta}>
						<span>Bitcoin network</span>
						<span>To your wallet</span>
					</div>
				</div>
			</div>
			{!review && currencies.length > 0 && (
				<div className={styles.quickCoins} aria-label="Popular source coins">
					<span>Pay with</span>
					{['usdc', 'eth', 'sol', 'usdt'].map((ticker) => {
						const coin =
							currencies.find(
								(item) =>
									item.ticker === ticker &&
									(ticker === 'sol' ? item.network === 'sol' : item.network === 'eth')
							) ?? currencies.find((item) => item.ticker === ticker)
						return coin ? (
							<button
								type="button"
								key={ticker}
								aria-pressed={sourceKey === keyOf(coin)}
								onClick={() => selectSource(coin)}
							>
								{ticker.toUpperCase()}
							</button>
						) : null
					})}
				</div>
			)}
			{catalogError && (
				<div className={styles.error} role="alert">
					{catalogError}
					<button type="button" onClick={() => setCatalogRetry((value) => value + 1)}>
						Retry coins
					</button>
				</div>
			)}
			{!catalogLoading && !catalogError && currencies.length === 0 && (
				<div className={styles.error} role="status">
					No source coins are available right now.
					<button type="button" onClick={() => setCatalogRetry((value) => value + 1)}>
						Retry coins
					</button>
				</div>
			)}
			{amount && !normalizedAmount && (
				<p className={styles.error} role="status">
					Enter a positive amount using numbers and a decimal point.
				</p>
			)}
			{belowMinimum && source && (
				<p className={styles.error} role="status">
					Enter at least {minimum} {source.ticker.toUpperCase()} for this pair.
				</p>
			)}
			{quoteError && (
				<div className={styles.error} role="alert">
					{quoteError}
					<button type="button" onClick={() => setQuoteRetry((value) => value + 1)}>
						Retry quote
					</button>
				</div>
			)}
			{quote && route && (
				<details className={styles.rateDetails} open={review || undefined}>
					<summary>
						<span>
							{route.providerName}
							<small>
								{route.provider === quote.recommendedProvider ? 'Best rate' : 'Selected'}
							</small>
						</span>
						<span>
							{quote.routes.length > 1 ? 'Compare rates' : 'Rate details'}
							<ChevronDown size={14} />
						</span>
					</summary>
					<div className={styles.routeSection}>
						<div className={styles.detailRow}>
							<span>Exchange provider</span>
							<span>
								{quote.routes.length > 1
									? route.provider === quote.recommendedProvider
										? 'Best return selected'
										: `${quote.routes.length} available quotes`
									: 'Available route'}
							</span>
						</div>
						<div className={styles.routes} role="radiogroup" aria-label="Exchange provider">
							{quote.routes.map((item, index) => (
								<label
									key={item.provider}
									className={styles.route}
									data-selected={route.provider === item.provider}
								>
									<input
										type="radio"
										name="swap-provider"
										value={item.provider}
										checked={route.provider === item.provider}
										disabled={creating}
										onChange={() => setProvider(item.provider)}
									/>
									<span>
										{item.providerName}
										{index === 0 && quote.routes.length > 1 && <small>Best</small>}
									</span>
									<strong>{formatBtc(item.toAmount)} BTC</strong>
								</label>
							))}
						</div>
						<div className={styles.detailRow}>
							<span>Rate</span>
							<span>
								1 {source?.ticker.toUpperCase()} ≈{' '}
								{formatBtc(route.toAmount / Number(normalizedAmount))} BTC
							</span>
						</div>
						{route.transactionSpeedForecast && (
							<div className={styles.detailRow}>
								<span>Estimated time</span>
								<span>{route.transactionSpeedForecast}</span>
							</div>
						)}
						<p className={styles.note}>
							Provider fees are included in the estimate. Your sending wallet may charge a network
							fee. The final BTC amount can change.
						</p>
						{route.warningMessage && <p className={styles.note}>{route.warningMessage}</p>}
					</div>
				</details>
			)}
			<div className={styles.destination}>
				<WalletIcon size={19} />
				<div>
					<span>Receiving wallet</span>
					{address ? (
						<code title={address}>{review ? address : shortAddress(address)}</code>
					) : (
						<span className={styles.muted}>Connect your OW wallet to receive BTC</span>
					)}
				</div>
				{address && <Check size={16} className={styles.destinationCheck} />}
			</div>
			{review && source && (
				<div className={styles.refund}>
					<label htmlFor="swap-refund">
						Refund address <span>{networkLabel(source.network)}</span>
					</label>
					<input
						id="swap-refund"
						autoComplete="off"
						spellCheck={false}
						value={refund}
						disabled={creating}
						onChange={(e) => setRefund(e.target.value)}
						placeholder={`Your ${networkLabel(source.network)} address`}
					/>
					<p className={styles.note}>
						Use an address you control on {networkLabel(source.network)} for any refund the
						provider approves. Refunds may require verification and fees.
					</p>
					{source.hasExternalId && (
						<>
							<label htmlFor="swap-refund-memo">
								Refund memo / destination tag <span>If required by your wallet</span>
							</label>
							<input
								id="swap-refund-memo"
								value={refundMemo}
								disabled={creating}
								onChange={(e) => setRefundMemo(e.target.value)}
								autoComplete="off"
							/>
						</>
					)}
				</div>
			)}
			{createError && (
				<p className={styles.error} role="alert">
					{createError}
				</p>
			)}
			{review && (
				<p className={styles.disclosure} id="swap-terms-notice">
					By clicking “Get deposit address,” you agree to the{' '}
					<a href="/terms" target="_blank" rel="noopener noreferrer">
						OW Terms
					</a>{' '}
					and{' '}
					<a
						href={
							route?.provider === 'changelly'
								? 'https://changelly.com/terms-of-use'
								: 'https://changenow.io/terms-of-use/changenow-terms'
						}
						target="_blank"
						rel="noopener noreferrer"
					>
						{route?.providerName} Terms
					</a>
					. The provider handles the exchange and may require identity verification. OW may earn a
					referral fee. You send funds yourself after reviewing the deposit instructions.
				</p>
			)}
			{!address ? (
				<button className={styles.primary} type="button" onClick={connect}>
					Connect wallet <ArrowUpRight size={18} />
				</button>
			) : review ? (
				<button
					className={styles.primary}
					type="button"
					onClick={create}
					aria-describedby="swap-terms-notice"
					disabled={!canReview || !refund.trim() || creating}
				>
					{creating ? (
						<>
							<Loader2 size={18} className={styles.spinner} /> Preparing deposit…
						</>
					) : (
						<>
							Get deposit address <ArrowUpRight size={18} />
						</>
					)}
				</button>
			) : (
				<button
					className={styles.primary}
					type="button"
					disabled={!canReview}
					onClick={() => setReview(true)}
				>
					{quoteLoading
						? 'Finding your rate…'
						: !normalizedAmount
						? 'Enter an amount'
						: 'Review swap'}
					{canReview && <ArrowUpRight size={18} />}
				</button>
			)}
			{!review && (
				<p className={styles.disclosure}>
					Powered by ChangeNOW &amp; Changelly. Availability depends on your location and provider
					requirements.
				</p>
			)}
			<CurrencyPicker
				open={pickerOpen}
				setOpen={setPickerOpen}
				currencies={currencies}
				selected={sourceKey}
				onSelect={selectSource}
			/>
		</div>
	)
}

function CurrencyPicker({
	open,
	setOpen,
	currencies,
	selected,
	onSelect
}: {
	open: boolean
	setOpen: (open: boolean) => void
	currencies: SwapCurrency[]
	selected: string
	onSelect: (coin: SwapCurrency) => void
}) {
	const [query, setQuery] = useState('')
	const [limit, setLimit] = useState(40)
	useEffect(() => {
		setQuery('')
		setLimit(40)
	}, [open])
	const filtered = useMemo(
		() =>
			currencies.filter((coin) =>
				`${coin.ticker} ${coin.name} ${networkLabel(coin.network)}`
					.toLowerCase()
					.includes(query.toLowerCase().trim())
			),
		[currencies, query]
	)
	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogContent className={styles.picker}>
				<DialogTitle>Select a coin</DialogTitle>
				<DialogDescription>Choose the coin and network you will send from.</DialogDescription>
				<div className={styles.search}>
					<Search size={18} />
					<input
						aria-label="Search coins or networks"
						autoComplete="off"
						placeholder="Search coins or networks"
						value={query}
						onChange={(e) => {
							setQuery(e.target.value)
							setLimit(40)
						}}
					/>
				</div>
				<div className={styles.coinList}>
					{filtered.slice(0, limit).map((coin) => (
						<button
							key={keyOf(coin)}
							type="button"
							onClick={() => onSelect(coin)}
							className={styles.coinOption}
							aria-label={`${coin.ticker.toUpperCase()} on ${networkLabel(coin.network)}`}
						>
							<CoinIcon coin={coin} />
							<span>
								<strong>
									{coin.ticker.toUpperCase()} <span>{coin.name}</span>
								</strong>
								<small>{networkLabel(coin.network)}</small>
							</span>
							{selected === keyOf(coin) && <Check size={18} />}
						</button>
					))}
					{!filtered.length && (
						<p className={styles.empty}>No coins found. Try a different coin or network.</p>
					)}
					<ScrollLoadMore
						hasMore={filtered.length > limit}
						itemCount={Math.min(filtered.length, limit)}
						onLoadMore={() => setLimit((value) => value + 40)}
					/>
				</div>
			</DialogContent>
		</Dialog>
	)
}

function SwapProgress({
	initial,
	address,
	persisted,
	onNew
}: {
	initial: SwapExchange
	address: string
	persisted: boolean
	onNew: () => void
}) {
	const [order, setOrder] = useState(initial)
	const orderRef = useRef(initial)
	const [saved, setSaved] = useState(persisted)
	const [error, setError] = useState('')
	const [retry, setRetry] = useState(0)
	const [now, setNow] = useState(() => Date.now())
	const status = order.status?.toLowerCase() || 'waiting'
	const done = isFinalSwap(order)
	const payoutAmount = getPayoutAmount(order)
	const needsStatus = !done || (status === 'finished' && payoutAmount === null)
	useEffect(() => {
		if (!needsStatus) return
		const controller = new AbortController()
		let timer: ReturnType<typeof setTimeout>
		const poll = async () => {
			try {
				const update = await swapRouter.status(order.id, order.provider, controller.signal)
				if (controller.signal.aborted) return
				const next = mergeExchangeUpdate(orderRef.current, update)
				orderRef.current = next
				setOrder(next)
				setSaved(saveActiveSwap(address, next))
				setError('')
			} catch (issue) {
				if (!controller.signal.aborted) setError(messageOf(issue))
			} finally {
				if (!controller.signal.aborted) timer = setTimeout(poll, 12_000)
			}
		}
		void poll()
		return () => {
			controller.abort()
			clearTimeout(timer)
		}
	}, [order.id, order.provider, address, needsStatus, retry])
	useEffect(() => {
		const timer = setInterval(() => setNow(Date.now()), 1000)
		return () => clearInterval(timer)
	}, [])
	const expires = order.validUntil ? Date.parse(order.validUntil) : NaN
	const depositExpired = Number.isFinite(expires) && now >= expires
	const waiting = isAwaitingSwapDeposit(order)
	const exact = getDepositAmountExact(order)
	const showDeposit = waiting && !depositExpired && !!exact && !!order.payinAddress
	const step =
		status === 'finished'
			? 3
			: status === 'sending'
			? 2
			: status === 'exchanging' || status === 'confirming'
			? 1
			: 0
	return (
		<div className={styles.progress}>
			<div className={styles.progressIcon}>
				{status === 'finished' ? <Check size={26} /> : <Clock3 size={25} />}
			</div>
			<h2 aria-live="polite" aria-atomic="true">
				{waiting && depositExpired
					? 'Deposit window expired'
					: swapFundsMoved(order) && (status === 'waiting' || status === 'new')
					? 'Deposit detected'
					: statusLabels[status] || 'Checking swap status'}
			</h2>
			<p className={styles.note}>
				{showDeposit
					? `Send only ${order.fromCurrency.toUpperCase()} on ${networkLabel(
							order.fromNetwork
					  )} to the address below.`
					: status === 'finished'
					? 'The provider has sent BTC to your receiving address. Bitcoin confirmations may still be pending.'
					: `Keep this order ID if you contact the exchange provider.${
							saved ? ' Your swap is saved in this browser.' : ''
					  }`}
			</p>
			{!saved && (
				<p className={styles.error} role="alert">
					Keep this tab open. Your browser could not save this swap. Copy the order ID before
					leaving.
				</p>
			)}
			{showDeposit && (
				<>
					<div className={styles.depositAmount}>
						<span>Send exactly</span>
						<strong>
							{exact} <small>{order.fromCurrency.toUpperCase()}</small>
						</strong>
						<button
							type="button"
							aria-label="Copy exact deposit amount"
							onClick={() => copyValue(exact!, 'Deposit amount')}
						>
							<Copy size={17} />
						</button>
					</div>
					<div className={styles.qr}>
						<QRCode value={order.payinAddress} size={160} />
					</div>
					<div className={styles.addressBlock}>
						<span>{networkLabel(order.fromNetwork)} deposit address</span>
						<code>{order.payinAddress}</code>
						<button type="button" onClick={() => copyValue(order.payinAddress, 'Deposit address')}>
							<Copy size={16} /> Copy address
						</button>
					</div>
					{order.payinExtraId && (
						<div className={styles.memo}>
							<strong>Required memo / destination tag</strong>
							<code>{order.payinExtraId}</code>
							<button type="button" onClick={() => copyValue(order.payinExtraId!, 'Memo')}>
								Copy memo
							</button>
							<span>
								Include this with your transfer so the provider can identify your deposit.
							</span>
						</div>
					)}
					{Number.isFinite(expires) && (
						<p className={styles.note}>
							Send before{' '}
							{new Date(expires).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}. Do
							not reuse this address for another swap.
						</p>
					)}
				</>
			)}
			{waiting && !depositExpired && !showDeposit && (
				<p className={styles.error}>
					Deposit details are not ready. Do not send funds yet. Refresh the order status or contact
					the provider.
				</p>
			)}
			<ol className={styles.steps}>
				{['Deposit', 'Exchange', 'Receive BTC'].map((label, i) => (
					<li key={label} data-active={step >= i + 1}>
						<span>{step >= i + 1 ? <Check size={12} /> : i + 1}</span>
						{label}
					</li>
				))}
			</ol>
			<div className={styles.detailRow}>
				<span>Provider</span>
				<strong>{order.providerName}</strong>
			</div>
			<div className={styles.detailRow}>
				<span>{status === 'finished' ? 'BTC sent' : 'Estimated BTC'}</span>
				<strong>
					{payoutAmount !== null ? `${formatBtc(payoutAmount)} BTC` : 'Awaiting provider'}
				</strong>
			</div>
			<div className={styles.addressBlock}>
				<span>Receiving wallet</span>
				<code>{order.payoutAddress}</code>
			</div>
			<div className={styles.detailRow}>
				<span>Order ID</span>
				<button
					type="button"
					className={styles.inlineCopy}
					onClick={() => copyValue(order.id, 'Order ID')}
				>
					{order.id}
					<Copy size={13} />
				</button>
			</div>
			{error && (
				<p role="alert" className={styles.error}>
					Status update failed: {error}{' '}
					{saved
						? 'Your existing order is still saved.'
						: 'Keep this tab open and copy your order ID.'}
				</p>
			)}
			{needsStatus && (
				<button
					type="button"
					className={styles.secondary}
					onClick={() => setRetry((value) => value + 1)}
				>
					<RefreshCw size={15} /> Refresh status
				</button>
			)}
			{order.payoutHash && /^[a-fA-F0-9]{64}$/.test(order.payoutHash) && (
				<a
					className={styles.secondary}
					href={`https://mempool.space/tx/${order.payoutHash}`}
					target="_blank"
					rel="noopener noreferrer"
				>
					View Bitcoin transaction <ExternalLink size={15} />
				</a>
			)}
			{done && (
				<button type="button" className={styles.primary} onClick={onNew}>
					Start another swap
				</button>
			)}
			<a
				className={styles.support}
				href={
					order.provider === 'changelly'
						? 'https://support.changelly.com/'
						: 'https://changenow.io/contact'
				}
				target="_blank"
				rel="noopener noreferrer"
			>
				{order.providerName} support <ArrowUpRight size={13} />
			</a>
		</div>
	)
}
